import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { Information } from "@/components/Information/Information";

import {
	MsdynamicsListSyncTriggerResponse,
	MsdynamicsSyncTriggerWithInherit,
} from "../../../../Company/schema/dynamics";

interface DynamicsSyncSectionProps {
	isDynamicsEnabled: boolean;
	isConnected: boolean;
	listSyncTrigger: MsdynamicsListSyncTriggerResponse | undefined;
	selectedDynamicsSyncTrigger: MsdynamicsSyncTriggerWithInherit | undefined;
	setSelectedDynamicsSyncTrigger: (trigger: MsdynamicsSyncTriggerWithInherit) => void;
}

/**
 * The list-level override of the Dynamics 365 **record sync** trigger — and nothing else.
 *
 * Unlike the HubSpot section there is no per-list opportunity-creation trigger to render:
 * `msdynamics_deal_creation_trigger` has a unique `companyId` and no list column, so that
 * setting exists once, in the company settings tab.
 */
export const DynamicsSyncSection: FunctionComponent<DynamicsSyncSectionProps> = ({
	isDynamicsEnabled,
	isConnected,
	listSyncTrigger,
	selectedDynamicsSyncTrigger,
	setSelectedDynamicsSyncTrigger,
}) => {
	const { t } = useTranslation();
	const tsSettings = (key: string) => t(`list.settings.${key}`);

	if (!isDynamicsEnabled || !isConnected) {
		return null;
	}

	return (
		<div className="flex flex-col w-full mt-8">
			<div className="flex flex-col gap-2 w-full mb-4">
				<div className="text-ssm font-medium text-text">
					{tsSettings("msdynamics.syncTrigger.title")}{" "}
					<Information information={tsSettings("msdynamics.syncTrigger.description")} />
				</div>
			</div>
			<Dropdown
				data={[
					{
						title: tsSettings("msdynamics.syncTrigger.INHERIT"),
						id: MsdynamicsSyncTriggerWithInherit.INHERIT,
					},
					{
						title: tsSettings("msdynamics.syncTrigger.DISABLED"),
						id: MsdynamicsSyncTriggerWithInherit.DISABLED,
					},
					{
						title: tsSettings("msdynamics.syncTrigger.ON_CAMPAIGN_START"),
						id: MsdynamicsSyncTriggerWithInherit.ON_CAMPAIGN_START,
					},
					{
						title: tsSettings("msdynamics.syncTrigger.ON_REPLY"),
						id: MsdynamicsSyncTriggerWithInherit.ON_REPLY,
					},
				]}
				defaultValue={
					selectedDynamicsSyncTrigger
						? {
								id: selectedDynamicsSyncTrigger,
								title: tsSettings(`msdynamics.syncTrigger.${selectedDynamicsSyncTrigger}`),
							}
						: undefined
				}
				floating={true}
				handleSelect={(value: DropdownAutoCompleteItem) => {
					setSelectedDynamicsSyncTrigger(value.id as MsdynamicsSyncTriggerWithInherit);
				}}
			/>
			{listSyncTrigger?.isInherited && (
				<div className="text-ssm text-text-meta mt-2">
					{tsSettings("msdynamics.syncTrigger.inheritedFrom")}:{" "}
					{tsSettings(`msdynamics.syncTrigger.${listSyncTrigger.effectiveTrigger}`)}
				</div>
			)}
		</div>
	);
};
